import type { ReactNode } from 'react'
import { isTeamId, type UnsavedTeam } from '@terros/common'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { useForm, isNotEmpty } from '@mantine/form'
import { TextInput, Group } from '@mantine/core'
import { useToastError } from '@hooks'
import { useProfile, useAddTeam, getLevelForChildTeam, useTeam, getLevelName, useTranslation } from '@front/model'
import { ErrorScreen, PageHeading, NotReady, ButtonAdd, Page } from '@components'
import { getAddTeamDestination, validateTeamAddSearch } from './navigation'

function Add(): ReactNode {
  const { parent: parentParam, returnTo } = Route.useSearch()
  const parentId = isTeamId(parentParam) ? parentParam : undefined

  const { data, isLoading: isLoadingParent, error: parentError } = useTeam(parentId)
  const { hasPermission, isSuccess: isProfileLoaded, company } = useProfile()
  const canAdd = hasPermission('team:add')
  const { add, isAdding, error } = useAddTeam()
  const { t } = useTranslation()
  useToastError(error)
  const navigate = useNavigate()

  const parent = data?.team
  const level = parent ? getLevelForChildTeam(parent.level) : company?.highestTeamLevel

  const form = useForm<UnsavedTeam>({
    initialValues: {
      level: level ?? 2,
      parentId,
      name: '',
    },
    validate: {
      name: isNotEmpty(t('Team name is required')),
    },
  })
  const { onSubmit, getInputProps, isDirty } = form

  const submit = async (values: UnsavedTeam): Promise<void> => {
    const saved = await add({ ...values, parentId, level: level ?? 2 })
    navigate({ href: getAddTeamDestination(saved.teamId, returnTo) })
  }

  if (parentId && (isLoadingParent || parentError))
    return <NotReady data={data} error={parentError} id='parent' isLoading={isLoadingParent} />

  if (isProfileLoaded && !canAdd) {
    return <ErrorScreen message={t('You do not have permission to add teams')} />
  }

  const addLevel = parent?.level ? parent.level - 1 : company?.highestTeamLevel
  const subteamName = getLevelName(company, addLevel ?? 2)

  return (
    <Page>
      <PageHeading back title={t('Add {{subteamName}}', { subteamName })} />
      <form onSubmit={onSubmit(submit)}>
        <Page.Content scrollArea>
          {parent && (
            <TextInput
              disabled
              label={t('Parent Team')}
              value={`${getLevelName(company, parent.level)}: ${parent.name}`}
            />
          )}
          <TextInput withAsterisk label={t('Team Name')} {...getInputProps('name')} />
          <Group justify='flex-end' mt='md'>
            <ButtonAdd disabled={!isDirty()} loading={isAdding} type='submit'>
              {t('Add {{subteamName}}', { subteamName })}
            </ButtonAdd>
          </Group>
        </Page.Content>
      </form>
    </Page>
  )
}

export const Route = createFileRoute('/team_/add/')({
  component: Add,
  validateSearch: validateTeamAddSearch,
})
